/**
 * Dialog do BOT por número WhatsApp. Spec 021: primeiro o MODO — quem conduz a
 * conversa antes de um humano:
 * - Sem bot: a conversa entra direto na fila da mesa do número;
 * - Chatbot do ERP (v8 Etapa 4): saudação, menu de opções (1..10 — cada uma
 *   aponta para um departamento e pode ter resposta automática), mensagem fora
 *   do expediente e aviso de encaminhamento;
 * - Bot externo (Zeepchat): o bot conversa até transferir; aqui só o
 *   mapeamento da fila que ele informa na transferência → mesa.
 * Trocar de modo não apaga a configuração do outro (voltar a ela a recupera).
 * Salva via PUT /whatsapp/channels/:id (integrations.manage — a tela já faz o
 * gate).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import type {
  WhatsappBotMode,
  WhatsappChannel,
  WhatsappChannelPatchBody,
  WhatsappChatbotMenuStyle,
} from '@erp/shared';
import {
  BOT_HANDOFF_QUEUE_MAX,
  BOT_HANDOFF_QUEUES_MAX,
  CHATBOT_HANDOFF_DEFAULT,
  CHATBOT_LABEL_MAX,
  CHATBOT_MENU_MAX_OPTIONS,
  CHATBOT_REPLY_MAX,
  WHATSAPP_BOT_MODE_LABELS,
  botQueueKey,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog } from '@/components/ui/misc';
import { Switch } from '@/components/ui/switch';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { WHATSAPP_KEY, errorMessage } from './whatsapp-queries';

/** Opção do menu em edição — deskId '' = departamento ainda não escolhido. */
interface OptionDraft {
  label: string;
  deskId: string;
  reply: string;
}

/** Linha do mapeamento fila → mesa do bot externo (spec 021). */
interface QueueDraft {
  queue: string;
  deskId: string;
}

const MODE_HINTS: Record<WhatsappBotMode, string> = {
  none: 'As conversas entram direto na fila da mesa do número.',
  erp: 'Novas conversas recebem a saudação e o menu de departamentos antes de chegar a um atendente.',
  external:
    'O bot da Zeepchat conversa com o cliente primeiro. As mensagens entram na conversa com o selo “com o bot” e ficam fora do “Precisa de resposta” até o bot transferir — aí a conversa entra na fila com uma nota trazendo o motivo, as variáveis e a conversa com o bot. Quando alguém da equipe pega a conversa — assumindo, respondendo, retomando ou iniciando —, o bot se cala. Ao encerrar, a conversa volta para o bot.',
};

export function ChatbotDialog({
  channel,
  onClose,
}: {
  channel: WhatsappChannel;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active);

  const [mode, setMode] = useState<WhatsappBotMode>(channel.botMode);
  const [menuStyle, setMenuStyle] = useState<WhatsappChatbotMenuStyle>(channel.chatbotMenuStyle);
  const [greeting, setGreeting] = useState(channel.chatbotGreeting ?? '');
  const [offhours, setOffhours] = useState(channel.chatbotOffhoursMessage ?? '');
  // spec 073 (P2): a pesquisa de satisfação ao finalizar vale em qualquer modo
  const [csat, setCsat] = useState(channel.csatEnabled);
  const [handoff, setHandoff] = useState(channel.chatbotHandoffMessage ?? '');
  const [options, setOptions] = useState<OptionDraft[]>(
    (channel.chatbotMenu ?? []).map((o) => ({
      label: o.label,
      deskId: String(o.deskId),
      reply: o.reply ?? '',
    }))
  );
  const [queues, setQueues] = useState<QueueDraft[]>(
    (channel.botHandoffQueues ?? []).map((q) => ({ queue: q.queue, deskId: String(q.deskId) }))
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (body: WhatsappChannelPatchBody) => {
      const res = await api<WhatsappChannel>(`/whatsapp/channels/${channel.id}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...WHATSAPP_KEY, 'channels'] });
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const setOption = (index: number, patch: Partial<OptionDraft>) =>
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, ...patch } : o)));

  const addOption = () => {
    if (options.length >= CHATBOT_MENU_MAX_OPTIONS) return;
    setOptions((prev) => [...prev, { label: '', deskId: '', reply: '' }]);
  };

  const removeOption = (index: number) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  const moveOption = (index: number, delta: -1 | 1) => {
    setOptions((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved === undefined) return prev;
      next.splice(target, 0, moved);
      return next;
    });
  };

  const setQueue = (index: number, patch: Partial<QueueDraft>) =>
    setQueues((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));

  const addQueue = () => {
    if (queues.length >= BOT_HANDOFF_QUEUES_MAX) return;
    setQueues((prev) => [...prev, { queue: '', deskId: '' }]);
  };

  const removeQueue = (index: number) => setQueues((prev) => prev.filter((_, i) => i !== index));

  /** corpo do modo ERP — as regras de antes da 021, intactas */
  const erpBody = (): WhatsappChannelPatchBody | null => {
    // Cada opção precisa de rótulo E departamento — a API valida deskIds (422
    // unknown_desk_ids) e o tamanho do menu (1..10), mas o feedback local é
    // mais claro que o código de erro cru.
    if (options.some((o) => o.label.trim() === '' || o.deskId === '')) {
      setError('Preencha o rótulo e o departamento de todas as opções do menu.');
      return null;
    }
    if (options.length === 0) {
      setError('Adicione ao menos uma opção ao menu para ativar o chatbot.');
      return null;
    }
    const menu = options.map((o) => {
      const reply = o.reply.trim();
      return {
        label: o.label.trim(),
        deskId: Number(o.deskId),
        ...(reply !== '' ? { reply } : {}),
      };
    });
    return {
      botMode: 'erp',
      chatbotMenuStyle: menuStyle,
      chatbotGreeting: greeting.trim() || null,
      chatbotMenu: menu,
      chatbotOffhoursMessage: offhours.trim() || null,
      chatbotHandoffMessage: handoff.trim() || null,
    };
  };

  /** corpo do bot externo: só o modo e o mapeamento (spec 021) */
  const externalBody = (): WhatsappChannelPatchBody | null => {
    if (queues.some((q) => q.queue.trim() === '' || q.deskId === '')) {
      setError('Preencha o nome da fila e a mesa de todas as linhas do mapeamento.');
      return null;
    }
    const seen = new Set<string>();
    for (const q of queues) {
      const key = botQueueKey(q.queue);
      if (seen.has(key)) {
        setError(`A fila “${q.queue.trim()}” aparece duas vezes — cada fila vai para uma mesa só.`);
        return null;
      }
      seen.add(key);
    }
    return {
      botMode: 'external',
      botHandoffQueues:
        queues.length > 0 ? queues.map((q) => ({ queue: q.queue.trim(), deskId: Number(q.deskId) })) : null,
      // spec 073 (P3): o aviso de fora do expediente na chegada à equipe
      chatbotOffhoursMessage: offhours.trim() || null,
    };
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = mode === 'erp' ? erpBody() : mode === 'external' ? externalBody() : { botMode: 'none' as const };
    if (body !== null) save.mutate({ ...body, csatEnabled: csat });
  };

  return (
    <Dialog open onClose={onClose} title={`Bot — ${channel.displayPhone}`} className="sm:max-w-xl">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="cb-mode">Quem atende primeiro</Label>
          <Select id="cb-mode" value={mode} onChange={(e) => setMode(e.target.value as WhatsappBotMode)}>
            {(['none', 'erp', 'external'] as const).map((m) => (
              <option key={m} value={m}>
                {WHATSAPP_BOT_MODE_LABELS[m]}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">{MODE_HINTS[mode]}</p>
        </div>

        {/* spec 073 (P2): a "Pesquisa de Satisfação" que a Blip Desk fazia */}
        <div className="flex items-start justify-between gap-4 rounded-md border px-3 py-2.5">
          <div className="min-w-0">
            <Label htmlFor="cb-csat">Pesquisa de satisfação ao finalizar</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Ao finalizar uma conversa, o cliente recebe a pergunta de 1 a 5. A nota aparece na
              Supervisão. Enquanto ele não responde (até 30 minutos), a próxima mensagem dele continua vindo
              para o ERP; uma resposta que não é nota abre conversa nova na mesma mesa.
            </p>
          </div>
          <Switch id="cb-csat" checked={csat} onCheckedChange={setCsat} aria-label="Pesquisa de satisfação ao finalizar" />
        </div>

        {mode === 'external' ? (
          <div className="space-y-1.5">
            <Label>
              Filas do bot → mesa{' '}
              <span className="font-normal text-muted-foreground">
                ({queues.length}/{BOT_HANDOFF_QUEUES_MAX})
              </span>
            </Label>
            <p className="text-xs text-muted-foreground">
              O nome da fila que o fluxo informa na transferência escolhe a mesa (maiúsculas e minúsculas
              não importam). Fila sem linha aqui, ou transferência sem fila, vai para a mesa do número.
            </p>
            {queues.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma fila mapeada — tudo vai para a mesa do número.</p>
            ) : (
              <ul className="space-y-2">
                {queues.map((q, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <Input
                      value={q.queue}
                      onChange={(e) => setQueue(i, { queue: e.target.value })}
                      placeholder="Fila (ex.: financeiro)"
                      aria-label={`Fila da linha ${i + 1}`}
                      maxLength={BOT_HANDOFF_QUEUE_MAX}
                      className="h-8"
                    />
                    <Select
                      value={q.deskId}
                      onChange={(e) => setQueue(i, { deskId: e.target.value })}
                      aria-label={`Mesa da fila da linha ${i + 1}`}
                      disabled={desksQ.isPending}
                      className="h-8"
                    >
                      <option value="" disabled>
                        Selecione a mesa
                      </option>
                      {desks.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.displayName}
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="text-muted-foreground hover:text-destructive"
                      title="Remover fila"
                      onClick={() => removeQueue(i)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addQueue}
              disabled={queues.length >= BOT_HANDOFF_QUEUES_MAX}
            >
              <Plus className="size-4" />
              Adicionar fila
            </Button>

            {/* spec 073 (P3): o mesmo texto do chatbot do ERP, agora também na
                chegada à equipe pelo bot externo */}
            <div className="space-y-1.5 pt-2">
              <Label htmlFor="cb-offhours-external">Mensagem fora do expediente</Label>
              <Textarea
                id="cb-offhours-external"
                value={offhours}
                onChange={(e) => setOffhours(e.target.value)}
                placeholder="Ex.: Estamos fora do horário de atendimento — retornaremos no próximo dia útil."
                className="min-h-16"
              />
              <p className="text-xs text-muted-foreground">
                Enviada ao cliente quando o bot transfere a conversa para uma mesa fora do expediente dela. Mesa
                sem expediente configurado atende 24 horas, e aí nada é enviado. O expediente fica em Mesas.
              </p>
            </div>
          </div>
        ) : null}

        {mode === 'erp' ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="cb-style">Estilo do menu</Label>
              <Select
                id="cb-style"
                value={menuStyle}
                onChange={(e) => setMenuStyle(e.target.value as WhatsappChatbotMenuStyle)}
              >
                <option value="interactive">Lista interativa</option>
                <option value="text">Texto numerado</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                A lista interativa é o menu nativo do WhatsApp; se o envio falhar, o menu sai como
                texto numerado automaticamente.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cb-greeting">Saudação</Label>
              <Textarea
                id="cb-greeting"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Ex.: Olá! Escolha uma opção para começar o atendimento."
                className="min-h-16"
              />
            </div>

            <div className="space-y-1.5">
              <Label>
                Opções do menu{' '}
                <span className="font-normal text-muted-foreground">
                  ({options.length}/{CHATBOT_MENU_MAX_OPTIONS})
                </span>
              </Label>
              {options.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma opção ainda.</p>
              ) : (
                <ul className="space-y-2">
                  {options.map((option, i) => (
                    <li key={i} className="space-y-2 rounded-md border border-divider p-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 shrink-0 text-center text-xs font-semibold text-muted-foreground tabular-nums">
                          {i + 1}.
                        </span>
                        <Input
                          value={option.label}
                          onChange={(e) => setOption(i, { label: e.target.value })}
                          placeholder="Rótulo da opção (ex.: Suporte técnico)"
                          aria-label={`Rótulo da opção ${i + 1}`}
                          maxLength={CHATBOT_LABEL_MAX}
                          className="h-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground"
                          title="Mover para cima"
                          disabled={i === 0}
                          onClick={() => moveOption(i, -1)}
                        >
                          <ArrowUp className="size-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground"
                          title="Mover para baixo"
                          disabled={i === options.length - 1}
                          onClick={() => moveOption(i, 1)}
                        >
                          <ArrowDown className="size-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground hover:text-destructive"
                          title="Remover opção"
                          onClick={() => removeOption(i)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </div>
                      <div className="space-y-2 pl-6.5">
                        <Select
                          value={option.deskId}
                          onChange={(e) => setOption(i, { deskId: e.target.value })}
                          aria-label={`Departamento da opção ${i + 1}`}
                          disabled={desksQ.isPending}
                          className="h-8"
                        >
                          <option value="" disabled>
                            Selecione o departamento
                          </option>
                          {desks.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.displayName}
                            </option>
                          ))}
                        </Select>
                        <Textarea
                          value={option.reply}
                          onChange={(e) => setOption(i, { reply: e.target.value })}
                          placeholder="Resposta automática (opcional)"
                          aria-label={`Resposta automática da opção ${i + 1}`}
                          maxLength={CHATBOT_REPLY_MAX}
                          className="min-h-9 text-sm"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOption}
                disabled={options.length >= CHATBOT_MENU_MAX_OPTIONS}
              >
                <Plus className="size-4" />
                Adicionar opção
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cb-offhours">Mensagem fora do expediente</Label>
              <Textarea
                id="cb-offhours"
                value={offhours}
                onChange={(e) => setOffhours(e.target.value)}
                placeholder="Ex.: Estamos fora do horário de atendimento — retornaremos no próximo dia útil."
                className="min-h-16"
              />
            </div>

            {/* spec 013: quando o cliente responde fora do menu duas vezes, o bot
                desiste — e avisa, em vez de deixar o cliente sem resposta */}
            <div className="space-y-1.5">
              <Label htmlFor="cb-handoff">Mensagem de encaminhamento</Label>
              <Textarea
                id="cb-handoff"
                value={handoff}
                maxLength={CHATBOT_REPLY_MAX}
                onChange={(e) => setHandoff(e.target.value)}
                placeholder={CHATBOT_HANDOFF_DEFAULT}
                className="min-h-16"
              />
              <p className="text-xs text-muted-foreground">
                Enviada quando o cliente não escolhe uma opção válida nem depois da repetição do menu. A
                conversa fica na fila com o selo “Aguardando atendente”. Em branco, usa o texto de
                exemplo.
              </p>
            </div>
          </>
        ) : null}

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
